import * as THREE from 'three';

// ============================================================
// Third-person chase camera
// ============================================================

const CAMERA_DISTANCE = 35;   // How far behind the aircraft
const CAMERA_HEIGHT = 12;     // How far above the aircraft
const CAMERA_LERP = 5;        // Smoothing speed
const LOOK_AHEAD = 100;       // How far ahead of aircraft the camera looks

// Reusable vectors
const _offset = new THREE.Vector3();
const _targetPos = new THREE.Vector3();
const _lookAt = new THREE.Vector3();
const _forward = new THREE.Vector3();
const _euler = new THREE.Euler();
const _noRollQuat = new THREE.Quaternion();

export class ChaseCamera {
  readonly camera: THREE.PerspectiveCamera;
  private readonly currentPos = new THREE.Vector3();
  private initialized = false;
  private reversed = false;
  private distance = CAMERA_DISTANCE;
  private height = CAMERA_HEIGHT;
  private shakeIntensity = 0;
  private readonly shakeDecay = 8;

  constructor(aspect: number, distance = CAMERA_DISTANCE, height = CAMERA_HEIGHT) {
    this.camera = new THREE.PerspectiveCamera(70, aspect, 1, 25000);
    this.distance = distance;
    this.height = height;

    window.addEventListener('keydown', (e) => {
      if (e.code === 'KeyT') this.reversed = !this.reversed;
    });
  }

  update(target: THREE.Object3D, dt: number): void {
    // Strip roll so the camera stays above the aircraft during rolls
    _euler.setFromQuaternion(target.quaternion, 'YXZ');
    _euler.z = 0;
    _noRollQuat.setFromEuler(_euler);

    // Behind or in front depending on mode
    const zDir = this.reversed ? -this.distance : this.distance;
    _offset.set(0, this.height, zDir);
    _offset.applyQuaternion(_noRollQuat);
    _targetPos.copy(target.position).add(_offset);

    // On first frame, snap to position
    if (!this.initialized) {
      this.currentPos.copy(_targetPos);
      this.initialized = true;
    }

    // Smooth follow
    const t = 1 - Math.exp(-CAMERA_LERP * dt);
    this.currentPos.lerp(_targetPos, t);
    this.camera.position.copy(this.currentPos);

    // Camera shake
    if (this.shakeIntensity > 0.01) {
      this.camera.position.x += (Math.random() - 0.5) * this.shakeIntensity;
      this.camera.position.y += (Math.random() - 0.5) * this.shakeIntensity;
      this.camera.position.z += (Math.random() - 0.5) * this.shakeIntensity;
      this.shakeIntensity *= Math.exp(-this.shakeDecay * dt);
    }

    // Look ahead or behind the aircraft
    const lookDir = this.reversed ? 1 : -1;
    _forward.set(0, 0, lookDir).applyQuaternion(target.quaternion);
    _lookAt.copy(target.position).addScaledVector(_forward, LOOK_AHEAD);
    this.camera.lookAt(_lookAt);
  }

  shake(intensity: number): void {
    this.shakeIntensity = Math.max(this.shakeIntensity, intensity);
  }

  onResize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }
}
